// measurementSystemUtils.js - Utilities for measurement system detection and conversion

/**
 * Check if a country uses the metric system
 * @param {String} country - Country name or code
 * @returns {Boolean} True if the country uses the metric system, false otherwise
 */
export const usesMetricSystem = (country) => {
  if (!country) return true; // Default to metric
  
  const normalizedCountry = country.toString().toLowerCase().trim();
  
  // List of countries that primarily use imperial system
  const imperialCountries = ['usa', 'united states', 'us', 'united states of america'];
  
  return !imperialCountries.includes(normalizedCountry);
};

/**
 * Get appropriate measurement system for a country
 * @param {String} country - Country name or code
 * @returns {String} 'metric' or 'imperial'
 */
export const getCountryMeasurementSystem = (country) => {
  return usesMetricSystem(country) ? 'metric' : 'imperial';
};

/**
 * Convert weight from metric to imperial
 * @param {Number} value - Value in metric units
 * @param {String} unit - Metric unit ('g' or 'kg')
 * @returns {Object} Converted value and unit in imperial
 */
export const metricToImperialWeight = (value, unit) => {
  if (unit === 'g') {
    return { value: value / 28.35, unit: 'oz' };
  } else if (unit === 'kg') {
    return { value: value / 0.45359, unit: 'lb' };
  }
  return { value, unit }; // Unknown unit, return as is
};

/**
 * Convert weight from imperial to metric
 * @param {Number} value - Value in imperial units
 * @param {String} unit - Imperial unit ('oz' or 'lb')
 * @returns {Object} Converted value and unit in metric
 */
export const imperialToMetricWeight = (value, unit) => {
  if (unit === 'oz') {
    return { value: value * 28.35, unit: 'g' };
  } else if (unit === 'lb') {
    return { value: value * 453.59, unit: 'g' };
  }
  return { value, unit }; // Unknown unit, return as is
};

/**
 * Convert volume from metric to imperial
 * @param {Number} value - Value in metric units
 * @param {String} unit - Metric unit ('ml' or 'l')
 * @returns {Object} Converted value and unit in imperial
 */
export const metricToImperialVolume = (value, unit) => {
  if (unit === 'ml') {
    return { value: value / 29.57, unit: 'fl oz' };
  } else if (unit === 'l') {
    return { value: value / 0.9464, unit: 'qt' };
  }
  return { value, unit }; // Unknown unit, return as is
};

/**
 * Convert volume from imperial to metric
 * @param {Number} value - Value in imperial units
 * @param {String} unit - Imperial unit ('fl oz', 'qt', etc.)
 * @returns {Object} Converted value and unit in metric
 */
export const imperialToMetricVolume = (value, unit) => {
  if (unit === 'fl oz') {
    return { value: value * 29.57, unit: 'ml' };
  } else if (unit === 'qt') {
    return { value: value * 946.4, unit: 'ml' };
  } else if (unit === 'gal') {
    return { value: value * 3785.4, unit: 'ml' };
  } else if (unit === 'pint') {
    return { value: value * 473.2, unit: 'ml' };
  }
  return { value, unit }; // Unknown unit, return as is
};

/**
 * Convert units between measurement systems
 * @param {Number} value - The value to convert
 * @param {String} fromUnit - Source unit
 * @param {String} toSystem - Target system ('metric' or 'imperial')
 * @returns {Object} Converted value and unit
 */
export const convertBetweenSystems = (value, fromUnit, toSystem) => {
  // Normalize the unit
  const unit = fromUnit.toLowerCase();
  
  // Determine what type of unit we're dealing with
  const isWeightMetric = ['g', 'kg', 'gram', 'kilogram'].includes(unit);
  const isWeightImperial = ['oz', 'lb', 'ounce', 'pound'].includes(unit);
  const isVolumeMetric = ['ml', 'l', 'milliliter', 'liter'].includes(unit);
  const isVolumeImperial = ['fl oz', 'qt', 'gal', 'pint', 'quart', 'gallon'].includes(unit);
  
  // Convert according to unit type and target system
  if (isWeightMetric && toSystem === 'imperial') {
    return metricToImperialWeight(value, unit === 'kg' ? 'kg' : 'g');
  } else if (isWeightImperial && toSystem === 'metric') {
    return imperialToMetricWeight(value, unit === 'lb' || unit === 'pound' ? 'lb' : 'oz');
  } else if (isVolumeMetric && toSystem === 'imperial') {
    return metricToImperialVolume(value, unit === 'l' || unit === 'liter' ? 'l' : 'ml');
  } else if (isVolumeImperial && toSystem === 'metric') {
    if (unit.includes('gal')) return imperialToMetricVolume(value, 'gal');
    if (unit.includes('qt') || unit.includes('quart')) return imperialToMetricVolume(value, 'qt');
    if (unit.includes('pint')) return imperialToMetricVolume(value, 'pint');
    return imperialToMetricVolume(value, 'fl oz');
  }
  
  // If we can't determine the conversion, return original values
  return { value, unit: fromUnit };
};